// src/components/ui/BeaconDot.js
//
// A small pulsing dot used to draw attention to a UI element.
// Rendered by the PARENT component, positioned relative to the target element.
//
// Usage:
//   <div className="relative inline-flex">
//     <SomeButton />
//     {guideVisible && <BeaconDot className="absolute -top-1 -right-1" />}
//   </div>
//
// The parent wrapper needs `position: relative` (or the Tailwind `relative` class).
// BeaconDot's className/style props are used to place it — typically absolute
// with a small negative offset so it overlaps the corner of the target.

import React from 'react';

/**
 * @param {string}  className  Tailwind/CSS classes for positioning (e.g. "absolute -top-1 -right-1")
 * @param {object}  style      Inline style overrides
 * @param {number}  size       Diameter of the solid dot in px (default: 8)
 */
const BeaconDot = ({ className = '', style = {}, size = 8 }) => (
  <span
    className={`inline-flex items-center justify-center pointer-events-none ${className}`}
    style={style}
    aria-hidden="true"
  >
    {/* Expanding pulse ring — grows outward and fades */}
    <span
      className="absolute rounded-full bg-app-accent"
      style={{
        width: size,
        height: size,
        opacity: 0.4,
        animation: 'beaconPulse 2s ease-out infinite',
      }}
    />
    {/* Solid dot */}
    <span
      className="relative rounded-full bg-app-accent"
      style={{ width: size, height: size }}
    />

    {/* Keyframes — injected once per render, deduplicated by the browser */}
    <style>{`
      @keyframes beaconPulse {
        0%   { transform: scale(1);   opacity: 0.4; }
        70%  { transform: scale(2.5); opacity: 0;   }
        100% { transform: scale(2.5); opacity: 0;   }
      }
    `}</style>
  </span>
);

export default BeaconDot;